import styled, { createGlobalStyle } from 'styled-components';
import { rgba } from 'polished';
import { H1, Subtitle } from '@omilia/typography';
import TextField from '@omilia/text-field';
import { colors } from '@omilia/theme';
import Icon from '@omilia/icon';
import Select, { AsyncSelect } from '@omilia/select';
import MultiLineTextField from '@omilia/multi-line-text-field';
import Pagination from '@omilia/pagination';
import { Field } from 'redux-form';

// @ts-ignore
import AutoCompleteMenuWrapper from 'components/AutoCompleteMenuWrapper';

export const SIconImage = styled(Icon)`
  color: ${colors.COLOR_BRAND};
  margin-bottom: 28px;
`;

export const SErrorInfoContainer = styled.div`
  margin-bottom: 40px;
`;

export const SErrorH1 = styled(H1)`
  color: ${colors.COLOR_GREY_1000};
  margin-bottom: 8px;
`;

export const SErrorSubtitle = styled(Subtitle)`
  color: ${colors.COLOR_GREY_800};
  white-space: pre-wrap;
`;

export const SEmailText = styled.span`
  color: ${colors.COLOR_BLUE_600};
`;

// TODO: Remove these style component, when
// we remove FOUNDATION. Right now it exists to
// override the css that FOUNDATION adds so the
// TextField component from UIKit looks as it should.
// and align with form
export const SFormTextField = styled(TextField)(
  ({ error }) => `
  &&& {
    border-bottom: 1px solid
      ${error ? colors.COLOR_RED_600 : colors.COLOR_GREY_500};
    border-left: none;
    border-top: none;
    border-radius: 0;
    border-right: none;
    box-shadow: none;
    color: ${colors.COLOR_GREY_1000};
    height: 31px;
    margin-bottom: 0;
    margin-top: 0;
    padding: 2px 0 8px 2px;
    z-index: initial;

    :focus {
      border-bottom: 2px solid
        ${error ? colors.COLOR_RED_600 : colors.COLOR_BLUE_600};

      & + label {
        color: ${error ? colors.COLOR_RED_600 : colors.COLOR_BLUE_600};
      }
    }

    :hover {
      border-bottom: 2px solid
        ${error ? colors.COLOR_RED_600 : colors.COLOR_GREY_800};
    }

    :hover:focus {
      border-bottom: 2px solid
        ${error ? colors.COLOR_RED_600 : colors.COLOR_BLUE_600};
    }

    // show up/down buttons on number input (override Foundation)
    &[type='number'] {
      -moz-appearance: number-input;

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        opacity: 1;
        -webkit-appearance: inner-spin-button;
      }
    }
  }

  ::placeholder {
    color: ${colors.COLOR_GREY_500};
  }

  & + label {
    color: ${error ? colors.COLOR_RED_600 : colors.COLOR_GREY_800};
    font-size: 12px;
    padding-left: 2px;
    transform: translateY(-20px);
  }

  /* password icon fix */
  & ~ span[role='button'] {
    cursor: pointer;
    top: 5px;
  }
`,
);

export const SFieldTransparent = styled(Field)`
  &&& {
    .react-select__control {
      background-color: transparent;
    }
  }
`;

export const SMultiLineTextField = styled(Field)`
  &&& {
    textarea {
      background: transparent;
    }
  }
`;

export const STransparentTextField = styled(Field)`
  &&& {
    background: transparent;
    height: 33px;
    padding-bottom: 0;
  }
`;

export const SAutoCompleteMenuWrapper = styled(AutoCompleteMenuWrapper)`
  &&& {
    margin-bottom: 24px;
    & > button {
      border: none;
      border-bottom: 1px solid ${colors.COLOR_GREY_500};
      border-radius: initial;
      color: ${colors.COLOR_GREY_1000};
      font-size: 14px;
      padding-left: 2px;

      & span {
        color: ${colors.COLOR_GREY_1000};
        font-weight: normal;
      }

      &:focus {
        box-shadow: none;
      }

      &::after {
        right: 2px;
      }
    }

    & > div > div:nth-child(1) {
      border: none;
      display: block;
    }

    & > div > div:nth-child(2) {
      border: 1px solid ${colors.COLOR_GREY_300};
      border-radius: 4px;
      box-shadow: 5px 10px 20px 0 ${rgba(`${colors.COLOR_BLACK}`, 0.15)};
      color: ${colors.COLOR_GREY_1000};
      max-height: 100px;
      top: 36px;

      & div {
        color: ${colors.COLOR_GREY_1000};
        font-size: 1rem;
        text-transform: initial;
      }
    }

    & > label {
      color: ${colors.COLOR_GREY_800};
      font-size: 1rem;
      font-weight: normal;
      margin-bottom: 3px;
      margin-left: 2px;
    }

    & input {
      font-size: 1rem;
      font-weight: normal;
      padding-left: 0px;
      text-indent: 2px;

      &:focus {
        border: none;
        border-bottom: 2px solid ${colors.COLOR_BLUE_600};
        border-radius: initial;
        box-shadow: none;
        outline: none;
      }
    }
  }
`;

// TODO: Remove when we drop Foundation
export const SSelect = styled(Select)`
  &&& {
    input[type='text'],
    [type='text'] {
      font-size: 1rem;
      font-weight: normal;
      height: 1rem;
      margin: 0;
      padding-left: 0;
      text-indent: 2px;

      &:focus {
        border: none;
        border-bottom: 2px solid ${colors.COLOR_BLUE_600};
        border-radius: initial;
        box-shadow: none;
        height: 1rem;
        margin: 0;
        outline: none;
      }
    }

    .react-select__option span,
    .react-select__single-value span {
      color: inherit;
    }
  }
`;

export const SAsyncSelect = styled(AsyncSelect)`
  &&& {
    input[type='text'],
    [type='text'] {
      font-size: 1rem;
      font-weight: normal;
      height: 1rem;
      margin: 0;
      padding-left: 0;
      text-indent: 2px;

      &:focus {
        border: none;
        border-bottom: 2px solid ${colors.COLOR_BLUE_600};
        border-radius: initial;
        box-shadow: none;
        height: 1rem;
        margin: 0;
        outline: none;
      }
    }

    .react-select__option span,
    .react-select__single-value span {
      color: inherit;
    }
  }
`;

export const SMultilineText = styled(MultiLineTextField)`
  &&& {
    textarea {
      border-radius: 0;
      height: inherit;
      min-height: inherit;

      &:focus {
        border-left: none;
        border-right: none;
        border-top: none;
      }
    }
  }
`;

// TODO: Remove me when we ditch Foundation
export const FixStyle = createGlobalStyle`
  button[type="submit"], button[type="button"] {
    border-radius: 4px;
  }
`;

export const FixChatButton = createGlobalStyle`
  button[type="submit"], button[type="button"] {
    border-radius: 4px;

    & > p {
      color: ${colors.COLOR_BLUE_600}
    }
  }
`;

// This css rules are a solution for modal body hidden overflowed items
export const FixModalOverflow = createGlobalStyle`
  div[data-test='modal-dialog-body'] {
    height: auto;
    min-height: 100%;
    overflow: visible;
  }
`;

export const SPagination = styled(Pagination)`
  margin-top: 57px;
  margin-right: 0px;
  margin-left: 0px;
  padding-left: 15px;
  padding-right: 10px;
  position: absolute;
  width: 100%;
  z-index: 99999;

  &&& {
    input {
      border: 1px solid ${colors.COLOR_GREY_600};
      border-radius: 4px;
      box-sizing: border-box;
      font: inherit;
      font-size: 1rem;
      font-weight: 400;
      height: 24px;
      line-height: 1.4285714285714286rem;
      margin-bottom: 0;
      outline: none;
      resize: vertical;
      text-align: center;
      width: 38px;
    }
  }
`;
